/**
 * A leased query, as something a visitor can read.
 *
 * The page never sends a query: it names one from `shared/queries.ts` and passes arguments, the
 * Worker builds it, and r2db leases exactly that. So "what is this page watching" has a precise
 * answer — the wire AST — and this renders it, because the AST is JSON with a `type` on every node
 * and nobody reads that for pleasure.
 *
 * It is deliberately NOT SQL. The shapes here have no SQL spelling that is both short and honest:
 * a `related` subquery is a nested result rather than a join, and the engine maintains it as one.
 * So the rendering keeps the engine's own words — `WHERE`, `ORDER BY`, `LIMIT`, `EXISTS`, and
 * `WITH` for the correlated children — and stays close enough to SQL to be read at a glance.
 *
 * It emits TOKENS rather than a string, and the string is the tokens joined. Highlighting a
 * rendering by re-reading it with regexes is guessing at what this function already knows — and
 * guessing wrong on exactly the input that matters here, since a project id or a search term is an
 * arbitrary string that can contain `and`, `issue` or a quote. So each piece says what it is on the
 * way out, and the panel only has to colour it.
 *
 * Pure, and tested in `test/query-text.test.ts`.
 */

type Node = Record<string, any>;

/** What a piece of a rendered query is, which is the whole of what highlighting needs to know. */
export type TokenKind = 'keyword' | 'table' | 'column' | 'literal' | 'alias' | 'fn' | 'plain';
export interface Token { kind: TokenKind; text: string }

const tok = (kind: TokenKind, text: string): Token => ({ kind, text });
const plain = (text: string) => tok('plain', text);
const kw = (text: string) => tok('keyword', text);
/** Several rendered pieces with something between them — `AND`s, commas, newlines. */
const join = (parts: Token[][], separator: Token[]): Token[] =>
  parts.flatMap((part, i) => (i ? [...separator, ...part] : part));
const AND = [plain(' '), kw('AND'), plain(' ')];

function literal(value: unknown): Token[] {
  if (Array.isArray(value)) return [plain('('), ...join(value.map(literal), [plain(', ')]), plain(')')];
  return [tok('literal', typeof value === 'string' ? `'${value}'` : value === null ? 'NULL' : String(value))];
}

/** One condition per entry: an `AND` of three is three of them, not a tree. */
function conditions(node: Node | undefined): Token[][] {
  if (!node) return [];
  if (node.type === 'and') return node.conditions.flatMap((c: Node) => conditions(c));
  if (node.type === 'or') return [[plain('('), ...join(node.conditions.flatMap((c: Node) => conditions(c)), [plain(' '), kw('OR'), plain(' ')]), plain(')')]];
  if (node.type === 'correlatedSubquery') {
    const sub = node.related.subquery;
    const where = conditions(sub.where);
    return [[
      kw(node.op), plain(' ('), tok('table', sub.table),
      ...(where.length ? [plain(' '), kw('WHERE'), plain(' '), ...join(where, AND)] : []),
      plain(')'),
    ]];
  }
  return [[tok('column', node.left?.name ?? '?'), plain(' '), kw(node.op), plain(' '), ...literal(node.right?.value)]];
}

const order = (by: [string, string][]): Token[] =>
  join(by.map(([column, dir]) => [tok('column', column), plain(' '), kw(dir.toUpperCase())]), [plain(', ')]);

/** A correlated child, as the one line it is worth: its alias, its table, and what it carries. */
function child(related: Node): Token[] {
  const sub = related.subquery;
  if (sub.aggregate === 'count') return [tok('fn', 'count'), plain('('), tok('table', sub.table), plain(') '), kw('AS'), plain(' '), tok('alias', sub.alias)];
  const inner: Token[][] = [];
  if (sub.where) inner.push([kw('WHERE'), plain(' '), ...join(conditions(sub.where), AND)]);
  if (sub.orderBy?.length) inner.push([kw('ORDER BY'), plain(' '), ...order(sub.orderBy)]);
  if (sub.limit) inner.push([kw('LIMIT'), plain(' '), tok('literal', String(sub.limit))]);
  if (sub.related?.length) inner.push([kw('WITH'), plain(' '), ...join(sub.related.map(child), [plain(', ')])]);
  return [
    tok('alias', sub.alias), plain(': '), tok('table', sub.table),
    ...(inner.length ? [plain(' ('), ...join(inner, [plain(' ')]), plain(')')] : []),
  ];
}

/** The whole query, one clause per line, each piece saying what it is. */
export function queryTokens(ast: Node): Token[] {
  const lines: Token[][] = [
    ast.aggregate === 'count'
      ? [tok('fn', 'count'), plain('(*) '), kw('OF'), plain(' '), tok('table', ast.table)]
      : [tok('table', ast.table)],
  ];
  const where = conditions(ast.where);
  if (where.length) {
    lines.push([kw('WHERE'), plain(' '), ...where[0]]);
    for (const rest of where.slice(1)) lines.push([plain('  '), kw('AND'), plain(' '), ...rest]);
  }
  if (ast.orderBy?.length) lines.push([kw('ORDER BY'), plain(' '), ...order(ast.orderBy)]);
  if (ast.one) lines.push([kw('LIMIT'), plain(' '), tok('literal', '1')]);
  else if (ast.limit) lines.push([kw('LIMIT'), plain(' '), tok('literal', String(ast.limit))]);
  // Five spaces, so a second child lands under the first rather than under `WITH`.
  if (ast.related?.length) lines.push([kw('WITH'), plain(' '), ...join(ast.related.map(child), [plain(',\n     ')])]);
  return join(lines, [plain('\n')]);
}

/** The same thing as text, for a test to assert and for anything that cannot colour. */
export const queryText = (ast: Node): string => queryTokens(ast).map(t => t.text).join('');

/** The arguments the page named, as one line: this is what actually crossed the wire. Built from
 *  the entries rather than from `JSON.stringify` of the whole, which spaces its commas by what
 *  happens to sit either side of them. */
export const argsText = (args: unknown): string =>
  !args || typeof args !== 'object' ? ''
    : Object.entries(args as Record<string, unknown>).map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join(', ');
